import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { Box } from 'konva/lib/shapes/Transformer';
import { memo, useCallback, useEffect, useRef } from 'react';
import { Group, Image, Rect, Transformer } from 'react-konva';
import useImage from 'use-image';
import { MIN_TRANFORMER_SIZE } from '../../consts';
import { ILogoImageState, IOnChangeLogoTransformerData } from '../../types';
import { getCorrectLogoCropSizes, turnRotation } from '../../utils';

interface ILogoKonvaImageProps {
  logo: ILogoImageState;
  primaryImage: HTMLImageElement | undefined;
  isForExport?: boolean;
  onDragEnd: (x: number, y: number) => void;
  onChangeTransformerData: (data: IOnChangeLogoTransformerData) => void;
}

export const LogoKonvaImage: React.FC<ILogoKonvaImageProps> = memo(
  ({ logo, primaryImage, isForExport, onDragEnd, onChangeTransformerData }) => {
    const {
      currentCropWidth,
      currentCropHeight,
      currentCropX,
      currentCropY,
      width,
      height,
      rotation,
      x,
      y,
      is_locked,
      currentImageUrl,
    } = logo;

    const trRef = useRef<Konva.Transformer | null>(null);
    const shapeRef = useRef<Konva.Rect | null>(null);
    const imageRef = useRef<Konva.Image | null>(null);

    const currentX = x || 0;
    const currentY = y || 0;
    const currentWidth = width || 0;
    const currentHeight = height || 0;
    const currentRotation = rotation || 0;

    const [logoImage, logoImageStatus] = useImage(currentImageUrl || '', 'anonymous');

    const canSeeTransformer = Boolean(!(is_locked || isForExport));
    const visibleTransformer = canSeeTransformer && logoImageStatus === 'loaded';

    const padding = 4;

    const { cropWidth, cropHeight } = getCorrectLogoCropSizes({
      image: logoImage,
      primaryImage: primaryImage || undefined,
      currentCropWidth: currentCropWidth,
      currentCropHeight: currentCropHeight,
      rectWidth: currentWidth,
      rectHeight: currentHeight,
    });

    useEffect(() => {
      if (trRef.current && shapeRef.current) {
        trRef.current.nodes([shapeRef.current]);
        trRef.current.getLayer()?.batchDraw();
      }
    });

    const handleTransform = useCallback((e: KonvaEventObject<Event>) => {
      const node = e.target;
      const imageNode = imageRef.current;

      if (!imageNode) return;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const rotation = node.rotation();
      const width = node.width() * scaleX;
      const height = node.height() * scaleY;
      const x = node.x();
      const y = node.y();

      node.scaleX(1);
      node.scaleY(1);
      node.x(x);
      node.y(y);
      node.width(width);
      node.height(height);
      node.rotation(rotation);

      imageNode.width(width);
      imageNode.height(height);
      imageNode.x(x);
      imageNode.y(y);
      imageNode.rotation(rotation);
    }, []);

    const handleTransformEnd = useCallback(
      (e: KonvaEventObject<Event>) => {
        const node = e.target;
        const imageNode = imageRef.current;

        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const rotation = turnRotation(node.rotation());
        const width = Math.round(node.width() * scaleX);
        const height = Math.round(node.height() * scaleY);
        const x = Math.round(node.x());
        const y = Math.round(node.y());

        node.scaleX(1);
        node.scaleY(1);
        node.x(x);
        node.y(y);
        node.width(width);
        node.height(height);
        node.rotation(rotation);

        imageNode?.width(width);
        imageNode?.height(height);
        imageNode?.x(x);
        imageNode?.y(y);
        imageNode?.rotation(rotation);

        onChangeTransformerData({
          x,
          y,
          width,
          height,
          rotation,
        });
      },
      [onChangeTransformerData]
    );

    const handleDragMove = useCallback((e: KonvaEventObject<Event>) => {
      const node = e.target;
      const imageNode = imageRef.current;

      if (!node || !imageNode) return;

      const newPosition = node.position();

      imageNode.position(newPosition);
      node.position(newPosition);
    }, []);

    const handleDragEnd = useCallback(
      (e: KonvaEventObject<Event>) => {
        const node = e.target;

        if (!node) return;

        const { x, y } = node.position();

        const correctX = Math.round(x);
        const correctY = Math.round(y);

        onDragEnd(correctX, correctY);
      },
      [onDragEnd]
    );

    const boundBoxFunc = useCallback((oldBox: Box, newBox: Box) => {
      if (newBox.width < MIN_TRANFORMER_SIZE || newBox.height < MIN_TRANFORMER_SIZE) {
        return oldBox;
      }

      return newBox;
    }, []);

    return (
      <Group>
        <Image
          ref={imageRef}
          image={logoImage}
          x={currentX}
          y={currentY}
          width={currentWidth}
          height={currentHeight}
          rotation={currentRotation}
          crop={{
            width: cropWidth,
            height: cropHeight,
            x: currentCropX,
            y: currentCropY,
          }}
          listening={false}
        />

        <Rect
          id={`logo-rect`}
          ref={shapeRef}
          x={currentX}
          y={currentY}
          width={currentWidth}
          height={currentHeight}
          rotation={currentRotation}
          stroke="#2680EB"
          strokeWidth={padding}
          hitStrokeWidth={padding}
          draggable
          onTransform={handleTransform}
          onTransformEnd={handleTransformEnd}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          visible={visibleTransformer}
          listening={!is_locked}
        />

        <Transformer
          ref={trRef}
          boundBoxFunc={boundBoxFunc}
          anchorStroke="#2680EB"
          anchorStrokeWidth={1}
          anchorCornerRadius={50}
          rotateAnchorOffset={20}
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          rotateEnabled={true}
          visible={visibleTransformer}
          listening={!is_locked}
        />
      </Group>
    );
  }
);
